import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../InputField";
import LoadingSpinner from "../LoadingSpinner";
import Switch from "../Switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { getGlobalChargingPointDefaultConfiguration } from "../../api/ChargingPoint";
import {
  get_configurations_grouped,
  put_change_configurations,
} from "../../api/Ocpp";
import {
  get_configurations_grouped as get_configurations_grouped_201,
  put_change_configurations as put_change_configurations_201,
} from "../../api/Ocpp201";
import { isOcpp201 } from "./ocppPointHelpers";
import { getLabel } from "../../helpers/configurationLabels";
import toast from "react-hot-toast";
import i18n from "../../i18n/i18n";

function isBoolString(value) {
  return (
    typeof value === "string" &&
    ["true", "false"].includes(value.toLowerCase())
  );
}

/** OCPP config values are strings; API/JSON may send booleans or 0/1 for bool keys. */
function normalizeValueToOcppString(value, config) {
  if (value === null || value === undefined) {
    return "";
  }

  const baselineStr = String(config.baselineValue ?? "");
  const draftStr = String(config.draftValue ?? "");
  const baselineBool = isBoolString(baselineStr);
  const draftBool = isBoolString(draftStr);
  const treatAsBool =
    baselineBool ||
    draftBool ||
    typeof value === "boolean";

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number" && treatAsBool) {
    if (value === 0) {
      return "false";
    }
    if (value === 1) {
      return "true";
    }
  }

  const str = String(value).trim();
  if (treatAsBool) {
    const lower = str.toLowerCase();
    if (lower === "true" || lower === "1") {
      return "true";
    }
    if (lower === "false" || lower === "0") {
      return "false";
    }
  }

  return str;
}

function formatValueForDisplay(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (isBoolString(value)) {
    return value.toLowerCase() === "true" ? i18n.t("common.yes") : i18n.t("common.no");
  }

  return String(value);
}

function configMatchesSearch(config, search) {
  if (!search.trim()) {
    return true;
  }

  const s = search.toLowerCase();
  const label = getLabel(config.name).toLowerCase();
  const name = (config.name || "").toLowerCase();
  const baseline = formatValueForDisplay(config.baselineValue).toLowerCase();
  const draftRaw = config.draftValue ?? "";
  const draftDisplay = isBoolString(draftRaw)
    ? formatValueForDisplay(draftRaw)
    : String(draftRaw);
  const draft = draftDisplay.toLowerCase();

  return (
    label.includes(s) ||
    name.includes(s) ||
    baseline.includes(s) ||
    draft.includes(s)
  );
}

function enrichFromServer(
  list,
  pendingDraftsByName,
  readOnlyGroup = false
) {
  return list
    .map((c) => ({
      ...c,
      readOnly: c.readOnly ?? c.readonly ?? false,
    }))
    .sort((a, b) => (a.readOnly === b.readOnly ? 0 : a.readOnly ? 1 : -1))
    .map((c) => {
      const baseline = c.value ?? "";
      const pending = pendingDraftsByName.get(c.name);
      const draft =
        pending !== undefined && !readOnlyGroup && !c.readOnly
          ? pending
          : baseline;

      return {
        ...c,
        baselineValue: baseline,
        draftValue: draft,
        updated: !readOnlyGroup && draft !== baseline,
      };
    });
}

const ConfigurationForm = ({
  chargingPoint,
}) => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [configurationsWithDefaults, setConfigurationsWithDefaults] = useState(
    []
  );
  const [configurationsWithoutDefaults, setConfigurationsWithoutDefaults] =
    useState([]);
  const [readonlyConfigurations, setReadonlyConfigurations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [fetchingGlobal, setFetchingGlobal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [configSearch, setConfigSearch] = useState("");

  const spinnerTimerRef = useRef(null);

  useEffect(() => {
    const isBusy = loading || fetchingGlobal;

    if (isBusy) {
      spinnerTimerRef.current = setTimeout(() => {
        setShowSpinner(true);
      }, 300);
    } else {
      if (spinnerTimerRef.current) {
        clearTimeout(spinnerTimerRef.current);
        spinnerTimerRef.current = null;
      }
      setShowSpinner(false);
    }

    return () => {
      if (spinnerTimerRef.current) {
        clearTimeout(spinnerTimerRef.current);
      }
    };
  }, [loading, fetchingGlobal]);

  const writableRowsRef = useRef({
    withDefaults: [],
    withoutDefaults: [],
  });

  useEffect(() => {
    writableRowsRef.current = {
      withDefaults: configurationsWithDefaults,
      withoutDefaults: configurationsWithoutDefaults,
    };
  }, [configurationsWithDefaults, configurationsWithoutDefaults]);

  const collectPendingDraftsFromRef = () => {
    const pending = new Map();
    const collect = (rows) => {
      rows.forEach((c) => {
        if (
          c.name &&
          !c.readOnly &&
          c.draftValue !== undefined &&
          c.baselineValue !== undefined &&
          c.draftValue !== c.baselineValue
        ) {
          pending.set(c.name, c.draftValue);
        }
      });
    };
    collect(writableRowsRef.current.withDefaults);
    collect(writableRowsRef.current.withoutDefaults);
    return pending;
  };

  const load = useCallback(
    async (preservePendingDrafts) => {
      const pointId = chargingPoint?.chargingPointPublicId || null;
      if (!pointId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = await getAccessToken();

        const pendingDraftsByName = preservePendingDrafts
          ? collectPendingDraftsFromRef()
          : new Map();

        const groupedConfigurations = isOcpp201(chargingPoint?.ocppVersion)
          ? await get_configurations_grouped_201(token, pointId)
          : await get_configurations_grouped(token, pointId);

        const withDef = enrichFromServer(
          groupedConfigurations?.configurationsWithDefaults || [],
          pendingDraftsByName,
          false
        );
        const withoutDef = enrichFromServer(
          groupedConfigurations?.configurationsWithoutDefaults || [],
          pendingDraftsByName,
          false
        );
        const ro = enrichFromServer(
          groupedConfigurations?.readonlyConfigurations || [],
          new Map(),
          true
        );

        setConfigurationsWithDefaults(withDef);
        setConfigurationsWithoutDefaults(withoutDef);
        setReadonlyConfigurations(ro);
        if (preservePendingDrafts) {
          toast.success(t("configuration.refreshed"));
        }
      } catch (e) {
        console.error(e);
        setError(e?.message || t("configuration.failedFetch"));
      } finally {
        setLoading(false);
      }
    },
    [chargingPoint?.chargingPointPublicId, chargingPoint?.ocppVersion, getAccessToken, t]
  );

  useEffect(() => {
    if (chargingPoint?.chargingPointPublicId) {
      load(false);
    }
  }, [chargingPoint?.chargingPointPublicId, getAccessToken, load]);

  const updateRowDraft = (name, draftValue) => {
    const apply = (prev) =>
      prev.map((c) =>
        c.name === name
          ? {
              ...c,
              draftValue,
              updated: draftValue !== c.baselineValue,
            }
          : c
      );
    setConfigurationsWithDefaults(apply);
    setConfigurationsWithoutDefaults(apply);
  };

  const fetchGlobalConfigs = async () => {
    setFetchingGlobal(true);

    try {
      const accessToken = await getAccessToken();
      const globalconfigs = await getGlobalChargingPointDefaultConfiguration(
        chargingPoint?.chargingPointPublicId,
        accessToken
      );

      const applyGlobals = (prev) =>
        prev.map((config) => {
          const globalConfig = globalconfigs.find(
            (gc) => gc.name === config.name
          );
          if (!globalConfig) {
            return config;
          }

          const normalizedGlobal = normalizeValueToOcppString(
            globalConfig.defaultValue,
            config
          );
          const normalizedDraft = normalizeValueToOcppString(
            config.draftValue,
            config
          );

          if (normalizedGlobal === normalizedDraft) {
            return config;
          }

          return {
            ...config,
            draftValue: normalizedGlobal,
            updated: normalizedGlobal !== config.baselineValue,
          };
        });

      setConfigurationsWithDefaults(applyGlobals);
      setConfigurationsWithoutDefaults(applyGlobals);

      toast.success(t("configuration.fetchedGlobal"));
    } catch (fetchError) {
      console.log(fetchError);
      toast.error(
        fetchError.response?.data?.message ||
          t("configuration.failedFetchGlobal")
      );
    } finally {
      setFetchingGlobal(false);
    }
  };

  const saveSingleRow = async (name) => {
    if (!chargingPoint?.chargingPointPublicId) {
      return;
    }

    const fromList = (rows) => rows.find((c) => c.name === name);
    const config =
      fromList(configurationsWithDefaults) ||
      fromList(configurationsWithoutDefaults);

    if (!config || config.readOnly) {
      return;
    }

    if (config.draftValue === config.baselineValue) {
      toast.error(t("configuration.noChangesToSave"));
      return;
    }

    const accessToken = await getAccessToken();
    setSavingKey(name);

    try {
      const data = {
        chargingPointPublicId: chargingPoint.chargingPointPublicId,
        changeConfigurations: [
          {
            key: name,
            value: config.draftValue ?? "",
          },
        ],
      };

      if (isOcpp201(chargingPoint?.ocppVersion)) {
        await put_change_configurations_201(accessToken, data);
      } else {
        await put_change_configurations(accessToken, data);
      }

      const commit = (prev) =>
        prev.map((c) =>
          c.name === name
            ? {
                ...c,
                baselineValue: c.draftValue,
                value: c.draftValue,
                updated: false,
              }
            : c
        );
      setConfigurationsWithDefaults(commit);
      setConfigurationsWithoutDefaults(commit);

      toast.success(t("toast.saved"));
    } catch (saveError) {
      console.log(saveError);
      toast.error(
        saveError.response?.data?.message ||
          t("configuration.failedSaveConfig")
      );
    } finally {
      setSavingKey(null);
    }
  };

  const renderEditableTable = (title, rows) => {
    if (!rows.length) {
      return null;
    }

    const filtered = rows.filter((c) => configMatchesSearch(c, configSearch));

    return (
      <>
        <div className="flex w-full gap-2 border-b-4 border-b-mute-200 pt-4 mb-4">
          <h5>{title}</h5>
        </div>
        {!filtered.length ? (
          <p className="mb-6 text-sm text-mute-500">
            {t("configuration.noMatchSearch")}
          </p>
        ) : (
          <div className="overflow-x-auto mb-6">
            <table className="w-full border text-xs sm:text-sm table-fixed">
              <thead className="bg-mute-100">
                <tr>
                  <th className="border px-1.5 py-1 sm:px-2 sm:py-1.5 text-left font-medium min-w-0">
                    {t("common.setting")}
                  </th>
                  <th className="border px-1.5 py-1 sm:px-2 sm:py-1.5 text-left font-medium hidden sm:table-cell min-w-0">
                    {t("common.current")}
                  </th>
                  <th className="border px-1.5 py-1 sm:px-2 sm:py-1.5 text-left font-medium min-w-0">
                    {t("common.newValue")}
                  </th>
                  <th className="border w-12 sm:w-14 px-1 py-1 text-center font-medium whitespace-nowrap">
                    <span className="sr-only">{t("common.save")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((config) => {
                  const labelName = getLabel(config.name);
                  const readOnly = config.readOnly ?? config.readonly ?? false;
                  const disabled =
                    readOnly || chargingPoint?.isOnline === false;
                  const isBool = isBoolString(config.draftValue);
                  const isDirty = config.updated;
                  const isSaving = savingKey === config.name;

                  return (
                    <tr
                      key={config.name}
                      className={
                        isDirty ? "border-2 border-amber-300 bg-warning-100" : ""
                      }
                    >
                      <td className="border px-1.5 py-1 sm:px-2 align-middle text-xs sm:text-sm font-semibold break-all min-w-0">
                        {labelName}
                        {readOnly ? ` (${t("common.readOnly")})` : ""}
                        <span className="block sm:hidden text-xs font-normal text-mute-500 mt-0.5 break-all">
                          {formatValueForDisplay(config.baselineValue)}
                        </span>
                      </td>
                      <td className="border px-1.5 py-1 sm:px-2 align-middle break-words whitespace-pre-wrap text-xs sm:text-sm hidden sm:table-cell min-w-0">
                        {formatValueForDisplay(config.baselineValue)}
                      </td>
                      <td className="border px-1.5 py-1 sm:px-2 align-middle min-w-0">
                        {isBool ? (
                          <div className="flex min-h-0 items-center">
                            <Switch
                              checked={config.draftValue === "true"}
                              onChange={(checked) =>
                                updateRowDraft(
                                  config.name,
                                  checked ? "true" : "false"
                                )
                              }
                              disabled={disabled}
                            />
                          </div>
                        ) : (
                          <textarea
                            className="input w-full min-h-8 sm:min-h-9 py-1 text-xs sm:text-sm leading-snug"
                            rows={1}
                            value={config.draftValue ?? ""}
                            disabled={disabled}
                            onChange={(e) =>
                              updateRowDraft(config.name, e.target.value)
                            }
                          />
                        )}
                      </td>
                      <td className="border w-12 sm:w-14 whitespace-nowrap px-1 py-1 align-middle">
                        <div className="flex min-h-8 sm:min-h-10 w-full items-center justify-center">
                          {!readOnly && (
                            <button
                              type="button"
                              className="btn btn-secondary inline-flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded p-0"
                              onClick={() => saveSingleRow(config.name)}
                              disabled={
                                disabled ||
                                isSaving ||
                                config.draftValue === config.baselineValue
                              }
                              title={isSaving ? t("common.saving") : t("configuration.saveToCharger")}
                              aria-label={
                                isSaving
                                  ? t("configuration.savingAria", { label: labelName })
                                  : t("configuration.saveAria", { label: labelName })
                              }
                            >
                              <FontAwesomeIcon icon={faCheck} className="text-xs sm:text-sm" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderReadonlyTable = (title, rows) => {
    if (!rows.length) {
      return null;
    }

    const filtered = rows.filter((c) => configMatchesSearch(c, configSearch));

    return (
      <>
        <div className="flex w-full gap-2 border-b-4 border-b-mute-200 pt-4 mb-4">
          <h5>{title}</h5>
        </div>
        {!filtered.length ? (
          <p className="mb-6 text-sm text-mute-500">
            {t("configuration.noMatchSearch")}
          </p>
        ) : (
          <table className="w-full border text-xs sm:text-sm table-fixed mb-6">
            <thead className="bg-mute-100">
              <tr>
                <th className="border px-1.5 py-1 sm:px-2 sm:py-1.5 text-left font-medium w-1/3 sm:w-1/4">
                  {t("common.setting")}
                </th>
                <th className="border px-1.5 py-1 sm:px-2 sm:py-1.5 text-left font-medium">
                  {t("common.value")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((config) => {
                const labelName = getLabel(config.name);
                const isBool = isBoolString(config.baselineValue);

                return (
                  <tr key={config.name}>
                    <td className="border px-1.5 py-1 sm:px-2 align-middle text-xs sm:text-sm font-medium break-all">
                      {labelName}
                    </td>
                    <td className="border px-1.5 py-1 sm:px-2 align-middle break-all whitespace-pre-wrap text-xs sm:text-sm">
                      {isBool
                        ? formatValueForDisplay(config.baselineValue)
                        : config.baselineValue ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </>
    );
  };

  if (loading && !configurationsWithDefaults.length && !configurationsWithoutDefaults.length && !readonlyConfigurations.length) {
    return (
      <div className="flex min-h-48 items-center justify-center py-8">
        <LoadingSpinner text={t("configuration.loading")}  />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-4">
        <p className="text-red-600">{error}</p>
        <button
          type="button"
          className="btn btn-mute mt-2"
          onClick={() => load(false)}
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const hasLoadedAnyConfigs =
    configurationsWithDefaults.length > 0 ||
    configurationsWithoutDefaults.length > 0 ||
    readonlyConfigurations.length > 0;

  const hasPendingEdits =
    configurationsWithDefaults.some((c) => c.updated) ||
    configurationsWithoutDefaults.some((c) => c.updated);

  return (
    <div className="w-full">
      <div className="mb-4 flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              fetchGlobalConfigs();
            }}
            disabled={loading || fetchingGlobal}
          >
            {fetchingGlobal ? t("common.fetching") : t("configuration.fetchGlobal")}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => load(true)}
            disabled={loading || fetchingGlobal}
          >
            {loading ? t("common.refreshing") : t("configuration.refreshFromCharger")}
          </button>
          <button
            type="button"
            className="btn btn-outline-mute"
            onClick={() => {
              const reset = (prev) =>
                prev.map((c) => ({
                  ...c,
                  draftValue: c.baselineValue,
                  updated: false,
                }));
              setConfigurationsWithDefaults(reset);
              setConfigurationsWithoutDefaults(reset);
            }}
            disabled={loading || fetchingGlobal || !hasPendingEdits}
          >
            {t("configuration.clearLocalEdits")}
          </button>
        </div>
        <div className="w-full shrink-0 lg:w-72">
          <InputField
            hideLabel
            icon={faSearch}
            placeholder={t("configuration.searchPlaceholder")}
            value={configSearch}
            onChange={(e) => setConfigSearch(e.target.value)}
          />
        </div>
      </div>

      {chargingPoint?.isOnline === false && (
        <p className="mb-4 text-sm text-amber-600 font-medium">
          {t("configuration.offlineCannotEdit")}
        </p>
      )}

      {showSpinner && hasLoadedAnyConfigs ? (
        <div className="flex min-h-48 items-center justify-center py-8">
          <LoadingSpinner
            text={fetchingGlobal ? t("configuration.fetchingGlobal") : t("configuration.refreshing")}
            delayMs={0}
          />
        </div>
      ) : (
        <>
          {renderEditableTable(
            t("configuration.ocppRequiredDefault"),
            configurationsWithDefaults
          )}
          {renderEditableTable(t("configuration.custom"), configurationsWithoutDefaults)}
          {renderReadonlyTable(t("configuration.readonly"), readonlyConfigurations)}
        </>
      )}
    </div>
  );
};

export default ConfigurationForm;
